import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/security/services/auth.service';
import { AppState } from '@core/store';
import { Logout } from '@core/store/actions/auth.actions';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import { auditTime, tap } from 'rxjs';
import Swal from 'sweetalert2';
// import { RespuestaApi } from '~shared/interface/RespuestaApi';
import { RespuestaApi } from '@utils/models/RespuestaApi';
import { CookiesService } from './cookies.service';
import { StateTabsService } from './StateTabs.service';

type ChannelMessage = 'idle-start' | 'keep-session' | 'close-session' | 'null';
const idleServiceConfig = {
	INACTIVITY_MINUTES: 120,
	MINUTES_TO_WARNING: 5,
	REFRESH_MINUTES: 55,
	RATE_INACTIVE_SECONDS: 5,
};
const idleChannel = 'idle-channel';
const lastInteractionIdle = 'last-interaction-idle';
const nextRefreshToken = 'next-refresh-token';

@Injectable({
	providedIn: 'root',
})
export class IdleService {
	private _audioAlerta = new Audio(
		'https://assets.mixkit.co/active_storage/sfx/765/765-preview.mp3'
	);

	private _isShowWarning: boolean = false;
	private _isShowAlert: boolean = false;
	private _isClosing: boolean = false;
	private _lastProcessedSignal: string | null = null;
	private _isResettingFromIdle: boolean = false;
	private _inactiveSecondsMultiplataform: number =
		idleServiceConfig.INACTIVITY_MINUTES * 60 * 1000 -
		idleServiceConfig.MINUTES_TO_WARNING * 60 * 1000 -
		idleServiceConfig.RATE_INACTIVE_SECONDS * 1000;

	private _mainInterval$: ReturnType<typeof setInterval> = setInterval(() => { });

	constructor(
		private _idle: Idle,
		private _keepalive: Keepalive,
		private cookiesService: CookiesService,
		private stateTabsService: StateTabsService,
		private router: Router,
		private store: Store<AppState>,
		private _authService: AuthService
	) {
		this._configTimesIdle();
		this._configureInterruptors();
		this._onIdleUser();
	}

	private _defineChanel(): void {
		if (this._mainInterval$) {
			clearInterval(this._mainInterval$);
		}

		this._mainInterval$ = setInterval(() => {
			const rawSignal = this.cookiesService.getItem(idleChannel);
			if (
				!rawSignal ||
				rawSignal === 'null' ||
				rawSignal === 'undefined' ||
				rawSignal === this._lastProcessedSignal
			) {
				return;
			}
			this._lastProcessedSignal = rawSignal;
			try {
				switch (rawSignal as ChannelMessage) {
					case 'idle-start':
						void this._showWarning();
						break;
					case 'keep-session':
						this._keepSession();
						break;
					case 'close-session':
						this._closeSession();
						break;
					case 'null':
						break;
				}
			} catch (e) {
				// console.error('Error al parsear la señal de la cookie', e);
			}
		}, 1000);
	}

	private _configTimesIdle(): void {
		this._idle.setIdle(idleServiceConfig.INACTIVITY_MINUTES * 60);
		this._idle.setTimeout(idleServiceConfig.MINUTES_TO_WARNING * 60);
		this._keepalive.interval(idleServiceConfig.REFRESH_MINUTES * 60);
	}

	private _configureInterruptors(): void {
		this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
	}

	private _onIdleUser(): void {
		this._idle.onIdleStart.subscribe(() => {
			const lastInteraction = this.cookiesService.getItem(lastInteractionIdle);
			const differenceTime = Date.now() - Number(lastInteraction);
			if (differenceTime < this._inactiveSecondsMultiplataform) {
				this._idle.watch();
				return;
			}
			this._setResponseIdleUser('idle-start');
			this._reproducirSonido();
		});
		this._idle.onInterrupt
			.pipe(auditTime(idleServiceConfig.RATE_INACTIVE_SECONDS * 100))
			.subscribe(() => {
				this.cookiesService.setItem(lastInteractionIdle, Date.now().toString());
			});
		this._idle.onIdleEnd.subscribe(() => {
			// Bloqueamos temporalmente la reacción del Keepalive
			this._isResettingFromIdle = true;
			// Liberamos el bloqueo después de un breve instante
			setTimeout(() => (this._isResettingFromIdle = false), 1000);
		});
		this._keepalive.onPing.subscribe(() => {
			if (this._isResettingFromIdle) return;
			this._refreshTokenKeppAlive();
		});
	}

	private _refreshTokenKeppAlive(): void {
		if (!this.cookiesService.getItem(environment.refreshToken)) {
			this.cookiesService.removeItem(nextRefreshToken);
			return;
		}
		const nextRefresh = this.cookiesService.getItem(nextRefreshToken);
		let applyRefresh = false;
		if (nextRefresh !== null && nextRefresh !== undefined && nextRefresh !== '') {
			const differenceTime = Number(nextRefresh) - Date.now();
			applyRefresh = differenceTime < idleServiceConfig.RATE_INACTIVE_SECONDS * 1000;
		} else {
			applyRefresh = true;
		}
		if (applyRefresh) {
			this.cookiesService.setItem(
				nextRefreshToken,
				(Date.now() + idleServiceConfig.REFRESH_MINUTES * 60 * 1000).toString()
			);
			this._authService
				.refreshToken()
				.pipe(
					tap((res: RespuestaApi) => {
						this._authService.updateTokens(res.idToken, res.accessToken, res.maestroSyncToken);
					})
				)
				.subscribe();
		}
	}

	startWatching(): void {
		const hasAuthToken = this.cookiesService.checkItem(environment.authTokenKey);
		// Si no hay token, usuario no está logueado, no hacer nada
		if (!hasAuthToken) {
			return;
		}
		this._idle.watch();
		this._keepalive.start();
		this._validateState();
		this._defineChanel();
	}

	stopWatching(): void {
		this._idle.stop();
		this._keepalive.stop();
	}

	private async _showWarning(): Promise<void> {
		if (!this._isShowWarning) {
			this._isShowWarning = true;
			let timerInterval: ReturnType<typeof setInterval>;
			await Swal.fire({
				backdrop: `rgba(0,0,0,0.4) blur(5px) !important`,
				title: '¿Sigues ahí?',
				html: `Tu sesión está por expirar.<br>Cierre en <b></b> segundos.`,
				showCancelButton: true,
				showConfirmButton: true,
				confirmButtonColor: '#0ABB87',
				cancelButtonColor: '#d33',
				confirmButtonText: 'Mantener sesión',
				cancelButtonText: 'Cerrar sesión',
				allowOutsideClick: false,
				allowEscapeKey: false,
				timer: idleServiceConfig.MINUTES_TO_WARNING * 60 * 1000,
				timerProgressBar: true,
				// didOpen: (): void => {
				onOpen: (): void => {
					const timer = Swal.getPopup()?.querySelector('b');
					timerInterval = setInterval(() => {
						const ms = Swal.getTimerLeft() ?? 0;

						const minutes = Math.floor(ms / 60000);
						const seconds = Math.floor((ms % 60000) / 1000);

						const formatted = `${minutes.toString().padStart(2, '0')}:${seconds
							.toString()
							.padStart(2, '0')}`;

						if (timer) {
							timer.textContent = formatted;
						}
					}, 250);
				},
				// willClose: (): void => {
				onClose: (): void => {
					clearInterval(timerInterval);
				},
			}).then((result) => {
				this._isShowWarning = false;
				if (result.isConfirmed) {
					this._keepalive.ping();
					this._setResponseIdleUser('keep-session');
					this._keepSession();
				} else if (result.dismiss === Swal.DismissReason.cancel) {
					if (this.stateTabsService.hasAnyUnsavedChanges()) {
						void this._showAlertMessage();
					} else {
						this._setResponseIdleUser('close-session');
						this._cleanAndCloseSession();
					}
				} else if (result.dismiss === Swal.DismissReason.timer) {
					this._setResponseIdleUser('close-session');
					this._cleanAndCloseSession();
				}
			});
		}
	}

	private async _showAlertMessage(isWarningLogout: boolean = true): Promise<void> {
		if (!this._isShowAlert) {
			this._isShowAlert = true;
			await Swal.fire({
				title: '¿Cerrar sesión de todos modos?',
				text: 'Detectamos que tienes información sin guardar en una o más pestañas.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#0ABB87',
				cancelButtonColor: '#d33',
				confirmButtonText: 'Sí, salir',
				cancelButtonText: 'No, revisar',
				allowOutsideClick: false,
				allowEscapeKey: false,
			}).then((result) => {
				this._isShowAlert = false;
				if (result.isConfirmed) {
					this._setResponseIdleUser('close-session');
					this._cleanAndCloseSession();
				} else if (result.dismiss && isWarningLogout) {
					this._keepalive.ping();
					this._setResponseIdleUser('keep-session');
					this._keepSession();
				}
			});
		}
	}

	private _keepSession(): void {
		if (this._isShowWarning) {
			this._isShowWarning = false;
			Swal.close();
		}
		this._idle.watch();
	}

	private _cleanAndCloseSession(): void {
		this._authService.logout(this.cookiesService.getItem(environment.authTokenKey)).subscribe();
		if (this.cookiesService.checkItem(environment.authTokenKey)) {
			this.cookiesService.removeItem(environment.authTokenKey);
			this.cookiesService.removeItem(environment.maestroSyncToken);
		}
		this._closeSession();
	}

	private _closeSession(): void {
		clearInterval(this._mainInterval$);
		this._idle.stop();
		this._keepalive.stop();
		if (this._isShowWarning) {
			this._isShowWarning = false;
			Swal.close();
		}
		if (!this._isClosing) {
			this._isClosing = true;
			this.store.dispatch(new Logout());
			void this.router.navigate(['/auth/login']);
		}
	}

	private _setResponseIdleUser(message: ChannelMessage): void {
		this.cookiesService.setItem(idleChannel, message);
	}

	logout(): void {
		if (this.stateTabsService.hasAnyUnsavedChanges()) {
			void this._showAlertMessage(false);
		} else {
			this._setResponseIdleUser('close-session');
			this._cleanAndCloseSession();
		}
	}

	private _reproducirSonido(): void {
		this._audioAlerta.currentTime = 0; // Reinicia el audio al principio
		this._audioAlerta.volume = 0.5; // Volumen del audio
		this._audioAlerta.play().catch(() => {
			// console.error('Audio bloqueado por el navegador');
		});
	}

	private _validateState(): void {
		const rawSignal = this.cookiesService.getItem(idleChannel);
		if (rawSignal === 'close-session') {
			this.cookiesService.setItem(idleChannel, 'null');
			this._reseteValues();
			return;
		}
		if (rawSignal === 'idle-start') {
			this.cookiesService.setItem(idleChannel, 'null');
		}
	}

	private _reseteValues(): void {
		this._lastProcessedSignal = null;
		this._isShowWarning = false;
		this._isShowAlert = false;
		this._isClosing = false;
		this._lastProcessedSignal = null;
		this._isResettingFromIdle = false;
	}
}

